import { inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ReducerManager, select, Store } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import { defaultIfEmpty, map, switchMap, take, tap } from 'rxjs/operators';
import { HttpParamsObject, HttpParamValue } from '../services/api/api.service';
import { FhirApiService } from '../services/api/fhir-api.sevice';
import { UserService } from '../services/user.service';
import { userFhirIdSelector } from '../store/selectors/user-fhir.selectors';
import {
  isUserRolePatientSelector,
  isUserRoleRepresentativeSelector,
} from '../store/selectors/user-role.selectors';
import { DataFhirAdapter, DataFhirModel, ID, ResourceOf } from './data.model';
import { DataService } from './data.service';
import { CategoryElement } from './partial/categories.model';
import { userInformation } from '../services/user-information';

const isEntryMatch = (entry: fhir4.BundleEntry) =>
  !entry?.search?.mode || entry?.search?.mode === 'match';
const setEntryInclude = (entry: fhir4.BundleEntry) => ({
  ...entry,
  search: { mode: 'include' },
});

export abstract class DataFhirService<
  Model extends DataFhirModel = DataFhirModel,
> extends DataService<Model> {
  // API
  protected currentFetchParams: HttpParamsObject;
  protected partnerCode: string;
  protected readonly resourceType: ResourceOf<Model>['resourceType'];
  protected abstract readonly defaultParamsObject: HttpParamsObject;
  protected readonly feInclude: string;
  protected readonly userInfo = userInformation();

  // List of current Entity requests which are not resolved (to avoid double requests)
  private entityRequests: { [entityId: string]: Observable<Model> } = {};

  /**
   * @param dataName Unique Name of Data (for store name and api baseurl)
   * @param adapter
   * @param fhirApi Service to handle Fhir Api
   * @param userService
   * @param store
   * @param manager
   */
  constructor(
    dataName: string,
    protected adapter: DataFhirAdapter<Model>,
    protected fhirApi: FhirApiService,
    protected userService: UserService,
    protected store: Store,
    protected manager: ReducerManager,
  ) {
    super(dataName, store, manager);

    this.resourceType = this.adapter.resourceType;
    this.userService.partnerCode$.subscribe(
      (code) => (this.partnerCode = code),
    );

    inject(TranslocoService).langChanges$.subscribe(() => this.clearData());
  }

  fetchData(paramsObject?: HttpParamsObject, selectedIds?: ID[]): void {
    this.setLoading(true);

    this.fetchData$(paramsObject, selectedIds).subscribe(
      () => {}, //NOSONAR
      (err) => this.setError(err),
      () => this.setLoading(false),
    );
  }

  fetchData$(
    paramsObject?: HttpParamsObject,
    selectedIds?: ID[],
  ): Observable<void> {
    return this.searchItems$(paramsObject).pipe(
      map((dataList) => {
        dataList = dataList.filter((x) => {
          return x !== undefined;
        });
        this.currentFetchParams = paramsObject || {};
        this.setItems(dataList);
        this.setActive(selectedIds);
      }),
    );
  }

  refreshData(): void {
    if (!this.currentFetchParams) return;

    this.refreshData$().subscribe();
  }

  refreshData$(): Observable<void> {
    if (!this.currentFetchParams) return of();

    return this.getSelectedIds().pipe(
      switchMap((selectedIds) =>
        this.fetchData$(this.currentFetchParams, selectedIds),
      ),
    );
  }

  fetchDataByPatientId(patientId: string): void {
    this.fetchData({ patient: `Patient/${patientId}` });
  }

  fetchDataByPatientId$(patientId: string): Observable<void> {
    return this.fetchData$({ patient: `Patient/${patientId}` });
  }

  fetchDataByPatientOid(patientOid): void {
    this.fetchData({ identifier: patientOid.system + '|' + patientOid.value });
  }

  fetchDataByPractitionerId(practitionerId: string): void {
    this.userService.resourceType$.subscribe((resourceType) => {
      if (resourceType === 'Practitioner') {
        this.fetchData({ practitioner: `Practitioner/${practitionerId}` });
      }
    });
  }

  fetchDataByPractitionerEntityId(practitionerId: string): void {
    this.userService.resourceType$.subscribe((resourceType) => {
      if (resourceType === 'Practitioner') {
        this.fetchData({ entity: `Practitioner/${practitionerId}` });
      }
    });
  }

  fetchDataByPerformerId(practitionerId: string): void {
    this.userService.resourceType$.subscribe((resourceType) => {
      if (resourceType === 'Practitioner') {
        this.fetchData({ performer: `Practitioner/${practitionerId}` });
      }
    });
  }

  /**
   * fetches data where current User is a patient (only if current user is a patient)
   */
  fetchMyData(): void {
    this.userService.resourceType$?.subscribe((resourceType) => {
      if (resourceType === 'Patient') {
        this.userService.resourceId$.subscribe((patientId) =>
          this.fetchDataByPatientId(patientId),
        );
      }
    });
  }

  searchItems$(
    paramsObject: HttpParamsObject = {},
    resourceType?,
    findInIncluded?: boolean,
  ): Observable<Model[]> {
    return this.fhirApi
      .search(
        resourceType || this.resourceType,
        this.getFullParams(paramsObject),
        this.dataName,
      )
      .pipe(
        switchMap((response: fhir4.Bundle) =>
          this.processSearchBundle$(response, findInIncluded),
        ),
      );
  }

  protected getFullParams(
    paramsObject: HttpParamsObject = {},
  ): HttpParamsObject {
    const fullParams = {
      ...this.defaultParamsObject,
    };

    if (paramsObject) {
      Object.entries(paramsObject).forEach(([key, value]) => {
        if (!fullParams[key]) {
          fullParams[key] = value;
        } else {
          const defaultValues = Array.isArray(fullParams[key])
            ? (fullParams[key] as ReadonlyArray<HttpParamValue>)
            : [fullParams[key]];

          const values = Array.isArray(value) ? value : [value];

          fullParams[key] = [
            ...defaultValues,
            ...values.filter((val) => !defaultValues.includes(val)),
          ];
        }
      });
    }

    return fullParams;
  }

  protected processSearchBundle$(
    bundle: fhir4.Bundle,
    findInIncluded?: boolean,
  ): Observable<Model[]> {
    const matchedEntries = findInIncluded
      ? (bundle.entry || []).filter(
          ({ resource }) => resource.resourceType === this.adapter.resourceType,
        )
      : (bundle.entry || []).filter(isEntryMatch);

    this.lastSearchResultBundle = bundle;
    const feIncludeIds = this.processFeIncludeIds(bundle, this.feInclude);
    const feIncludeRequests = Object.entries(feIncludeIds).map(
      ([resourceType, ids]) => {
        return this.fhirApi.search(resourceType, { _id: ids.join(',') });
      },
    );

    return forkJoin(feIncludeRequests).pipe(
      defaultIfEmpty([]),
      switchMap((feIncludeBundles) => {
        const fullBundle = {
          ...bundle,
          entry: [...(bundle.entry || [])],
        };

        feIncludeBundles.forEach(({ entry = [] }) => {
          fullBundle.entry = [
            ...fullBundle.entry,
            ...entry.map(setEntryInclude),
          ];
        });

        return forkJoin(
          matchedEntries.map((entry: fhir4.BundleEntry) =>
            this.adapter
              .fromResource$(entry.resource, fullBundle)
              .pipe(defaultIfEmpty(null)),
          ),
        ).pipe(
          defaultIfEmpty([]),
          map((entities) =>
            entities.filter((entity: Model | null): entity is Model => {
              if (entity == null) return false;

              // filter list of entities only to those which have required categories
              const requiredCategoriesNames = this.adapter.categorySettings
                .filter(({ required }) => required)
                .map(({ categoryName }) => categoryName);

              return requiredCategoriesNames?.length === 0
                ? true
                : requiredCategoriesNames.some(
                    (categoryName) =>
                      entity?._categories?.[categoryName]?.length,
                  );
            }),
          ),
        );
      }),
    );
  }

  private processFeIncludeIds(
    bundle: fhir4.Bundle,
    feInclude: string = '',
  ): { [resourceType: string]: string[] } {
    const isFhirReference = (obj) => typeof obj?.reference === 'string';
    const isResourceInBundle = ({ resourceType, id }) =>
      bundle.entry.find(
        (e) => e.resource.resourceType === resourceType && e.resource.id === id,
      );
    const includedResourcesIds = {};

    const [resourceFrom, referenceParamName, includeResourceType] =
      feInclude.split(':');

    const matchedEntries = (bundle.entry || []).filter(
      (entry: fhir4.BundleEntry) => {
        const isMatched = isEntryMatch(entry);
        return isMatched && entry?.resource?.resourceType === resourceFrom;
      },
    );

    // Use JSON.stringify for deep search in object
    JSON.stringify(matchedEntries, (key, value) => {
      if (key !== referenceParamName) return value;
      if (!isFhirReference(value)) return value;

      const [resourceType, id] = value.reference.split('/');

      if (!resourceType || !id) return value;
      if (isResourceInBundle({ resourceType, id })) return value;
      if (includeResourceType && resourceType !== includeResourceType)
        return value;
      if (includedResourcesIds[resourceType]?.includes(id)) return value;

      includedResourcesIds[resourceType] = [
        ...(includedResourcesIds[resourceType] || []),
        id,
      ];

      return value;
    });

    return includedResourcesIds;
  }

  getItemById$(id: string, disableCache: boolean = false): Observable<Model> {
    let item$;

    if (this.hasEntity(id) && !disableCache) {
      item$ = this.getStoreItem(id).pipe(take(1));
    } else if (this.entityRequests[id]) {
      item$ = this.entityRequests[id];
    } else {
      item$ = this.fhirApi.read(this.resourceType, id, this.dataName).pipe(
        switchMap((resourceItem: ResourceOf<Model>) => {
          const item$ = this.adapter.fromResource$(resourceItem);

          return item$;
        }),
        tap((item) => {
          this.addStoreItem(item);
          delete this.entityRequests[id];
        }),
      );

      this.entityRequests[id] = item$;
    }

    return item$;
  }

  /**
   * saves new item to server
   * @param item
   * @param patientId
   */
  addItem(item: Model, patientId?: string): void {
    this.setLoading(true);

    this.addItem$(item, { patientId }).subscribe(
      (res) => this.refreshData(),
      (err) => this.setError(err),
      () => this.setLoading(false),
    );
  }

  addItem$(item: Model, options: { [key: string]: any }): Observable<any> {
    const resource = this.adapter.toResource(item, options);
    return this.fhirApi.handleResource(resource, this.dataName);
  }

  /**
   * adds item where current user is a patient (only if current user is a patient)
   */
  addItemToMe(item: Model): void {
    this.setLoading(true);

    this.addItemToMe$(item).subscribe(
      (res) => this.refreshData(),
      (err) => this.setError(err),
      () => this.setLoading(false),
    );
  }

  addItemToMe$(item: Model | any): Observable<any> {
    return forkJoin({
      isUserRolePatient: this.store.pipe(
        select(isUserRolePatientSelector),
        take(1),
      ),
      isUserRoleRepresentative: this.store.pipe(
        select(isUserRoleRepresentativeSelector),
        take(1),
      ),
      resourceId: this.store.pipe(select(userFhirIdSelector), take(1)),
    }).pipe(
      switchMap(
        ({ isUserRolePatient, isUserRoleRepresentative, resourceId }) => {
          if (isUserRolePatient) {
            return this.addItem$(item, { patientId: resourceId });
          } else if (isUserRoleRepresentative) {
            return this.addItem$(item, { patientId: item?.patientId });
          } else {
            return of(null);
          }
        },
      ),
    );
  }

  updateItem(item: Model, headers?): void {
    this.setLoading(true);

    const resource = this.adapter.toResource(item);

    this.fhirApi.handleResource(resource, this.dataName, headers).subscribe(
      (res) => this.refreshData(),
      (err) => this.setError(err),
      () => this.setLoading(false),
    );
  }

  updateItem$(item: Model, headers?): Observable<any> {
    const resource = this.adapter.toResource(item);
    return this.fhirApi.handleResource(resource, this.dataName, headers);
  }

  deleteItemById$(id: ID, params?): Observable<any> {
    return this.fhirApi
      .delete(this.resourceType, `${id}`, this.dataName, params)
      .pipe(tap(() => this.deselectItemById(id)));
  }

  deleteItemById(id: ID, params?): void {
    this.setLoading(true);

    this.deleteItemById$(id, params).subscribe(
      () => this.refreshData(),
      (err) => this.setError(err),
      () => this.setLoading(false),
    );
  }

  deleteItemsList$(dataList: (Model | ResourceOf<Model>)[]): Observable<any> {
    const requests = dataList.map(({ id }) => this.deleteItemById$(id));
    return forkJoin(requests);
  }

  deleteItemsList(dataList: (Model | ResourceOf<Model>)[]): void {
    this.setLoading(true);
    this.deleteItemsList$(dataList).subscribe(
      () => this.refreshData(),
      (err) => this.setError(err),
      () => this.setLoading(false),
    );
  }

  getCategoryOptionsList$(name: string): Observable<CategoryElement[]> {
    return this.adapter.getCategoryOptionsList$(name);
  }
}
